/**
 * Overtime Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as overtimeService from '../api/services/overtime.service';
import type {
  OvertimeApplyRequest,
  OvertimeCalculateRequest,
} from '../types';
import { toast } from '../stores/toast.store';

const KEYS = {
  init: ['overtime', 'init'] as const,
  types: ['overtime', 'types'] as const,
  applications: ['overtime', 'applications'] as const,
  application: (id: number) => ['overtime', 'applications', id] as const,
};

/**
 * Hook for fetching overtime init data (types + time options)
 */
export function useOvertimeInit() {
  return useQuery({
    queryKey: KEYS.init,
    queryFn: async () => {
      const result = await overtimeService.getOvertimeInit();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch overtime data');
      }
      return result.data!;
    },
  });
}

interface UseOvertimeApplicationsParams {
  offset?: number;
  limit?: number;
  state?: string;
}

/**
 * Hook for fetching overtime applications list
 */
export function useOvertimeApplications(params: UseOvertimeApplicationsParams = {}) {
  return useQuery({
    queryKey: [...KEYS.applications, params],
    queryFn: async () => {
      const result = await overtimeService.getOvertimeApplications(params);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch applications');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for fetching single overtime application
 */
export function useOvertimeApplication(id: number | undefined) {
  return useQuery({
    queryKey: KEYS.application(id!),
    queryFn: async () => {
      const result = await overtimeService.getOvertimeApplication(id!);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch application');
      }
      return result.data!;
    },
    enabled: !!id,
  });
}

/**
 * Hook for detecting overtime type by date
 */
export function useDetectOvertimeType() {
  return useMutation({
    mutationFn: async (date: string) => {
      const result = await overtimeService.detectOvertimeType(date);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to detect type');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for calculating overtime hours
 */
export function useCalculateOvertimeHours() {
  return useMutation({
    mutationFn: async (data: OvertimeCalculateRequest) => {
      const result = await overtimeService.calculateOvertimeHours(data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to calculate hours');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for creating overtime application
 */
export function useCreateOvertime() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: OvertimeApplyRequest) => {
      const result = await overtimeService.createOvertimeApplication(data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to create application');
      }
      return result.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.applications });
      toast.success('加班申請已建立');
    },
    onError: (error: Error) => {
      toast.error('建立失敗', error.message);
    },
  });
}

/**
 * Hook for submitting overtime application
 */
export function useSubmitOvertime() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await overtimeService.submitOvertimeApplication(id);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to submit');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.applications });
      queryClient.invalidateQueries({ queryKey: KEYS.application(data.id) });
      toast.success('加班申請已送出');
    },
    onError: (error: Error) => {
      toast.error('送出失敗', error.message);
    },
  });
}

/**
 * Hook for cancelling overtime application
 */
export function useCancelOvertime() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await overtimeService.cancelOvertimeApplication(id);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to cancel');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.applications });
      queryClient.invalidateQueries({ queryKey: KEYS.application(data.id) });
      toast.success('加班申請已撤銷');
    },
    onError: (error: Error) => {
      toast.error('撤銷失敗', error.message);
    },
  });
}

/**
 * Hook for deleting overtime application
 */
export function useDeleteOvertime() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await overtimeService.deleteOvertimeApplication(id);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to delete');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.applications });
      toast.success('加班申請已刪除');
    },
    onError: (error: Error) => {
      toast.error('刪除失敗', error.message);
    },
  });
}
